import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Typography,
  Grid,
  Paper,
} from '@mui/material';
import { useForm, Controller, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLocations } from '../../hooks/useLocations';
import { useCreateJob, useUpdateJob, type JobCreate, type JobUpdate } from '../../hooks/useJobs';
import { 
  CapacityEditor, 
  TimeWindowsEditor, 
  SkillsSelector, 
  FormSection,
  type TimeWindow,
  type Skill 
} from '../common';
import { getTimeFieldLabel, getTimeFieldHelperText, minutesToSeconds, secondsToMinutes } from '../../utils/timeFormat';

// Enhanced job form schema with complex constraints
const jobSchema = z.object({
  description: z.string().min(1, 'Job description is required').max(255, 'Description must be less than 255 characters'),
  location_id: z.number().min(1, 'Location selection is required'),
  setup: z.number().min(0, 'Setup time must be non-negative').default(0),
  service: z.number().min(0, 'Service time must be non-negative').default(300),
  // Multi-dimensional delivery/pickup arrays
  delivery: z.array(z.number().min(0, 'Delivery amounts must be non-negative')).default([]),
  pickup: z.array(z.number().min(0, 'Pickup amounts must be non-negative')).default([]),
  // Enhanced skills integration
  skills: z.array(z.number()).optional().default([]),
  // Enhanced priority system with validation
  priority: z.number().min(1, 'Priority must be at least 1').max(5, 'Priority cannot exceed 5').default(3),
  // Complex time windows management
  time_windows: z.array(z.object({
    start: z.number().min(0, 'Start time must be non-negative').max(86400, 'Start time cannot exceed 24 hours'),
    end: z.number().min(0, 'End time must be non-negative').max(86400, 'End time cannot exceed 24 hours')
  }).refine(data => data.start < data.end, {
    message: "End time must be after start time"
  })).default([]) as z.ZodType<TimeWindow[]>,
}).refine(data => {
  // Cross-field validation: if delivery or pickup is specified, at least one dimension should be > 0
  const hasDelivery = data.delivery.some(d => d > 0);
  const hasPickup = data.pickup.some(p => p > 0);
  
  if (data.delivery.length > 0 || data.pickup.length > 0) {
    return hasDelivery || hasPickup;
  }
  return true;
}, {
  message: "If delivery or pickup arrays are specified, at least one dimension must be greater than 0",
  path: ["delivery"]
});

type JobFormData = z.infer<typeof jobSchema>;

interface JobFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: (data: JobFormData) => void;
  projectId: number;
  job?: any; // For edit mode - existing job data
  initialData?: Partial<JobFormData>;
  onSuccess?: () => void; // Callback for successful submission
}
export const JobForm: React.FC<JobFormProps> = ({ 
  open, 
  onClose, 
  onSubmit, 
  projectId, 
  job, // Existing job for edit mode
  initialData, 
  onSuccess 
}) => {
  // Unified VRP architecture - get scenario and dataset context
  const { currentScenario, currentDataset } = null /* TODO: Replace with Toolpad navigation */;
  
  // Use dataset-aware location fetching for context-specific locations
  const locationOptions = currentDataset?.id ? {
    dataset_id: currentDataset.id,
    scenario_id: currentDataset.scenario_id,
    dataset_name: currentDataset.name,
    dataset_version: currentDataset.version || 1,
    enabled: true
  } : { enabled: true };
  
  const { data: locations, isLoading: locationsLoading } = useLocations(
    projectId, 
    currentScenario?.id, 
    locationOptions
  );
  const createJob = useCreateJob();
  const updateJob = useUpdateJob(); // Add the update job hook
  
  // Context validation
  const hasValidContext = projectId && currentScenario && currentDataset;
  
  // Determine if we're in edit mode
  const isEditMode = !!job;
  
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting }
  } = useForm<JobFormData>({
    resolver: zodResolver(jobSchema),
    defaultValues: {
      description: job?.description || initialData?.description || '',
      location_id: job?.location_id || initialData?.location_id || 0,
      setup: secondsToMinutes(job?.setup || initialData?.setup || 0),
      service: secondsToMinutes(job?.service || initialData?.service || 300),
      delivery: job?.delivery || initialData?.delivery || [],
      pickup: job?.pickup || initialData?.pickup || [],
      skills: job?.skills || initialData?.skills || [],
      priority: job?.priority || initialData?.priority || 3,
      time_windows: job?.time_windows || initialData?.time_windows || [],
    },
  });

  const selectedLocationId = watch('location_id');
  const safeLocations = Array.isArray(locations) ? locations : [];
  const selectedLocation = safeLocations.find(loc => loc.id === selectedLocationId);

  // Reset form when job prop changes (for edit mode)
  useEffect(() => {
    if (job) {
      console.log('🔄 [JobForm] Resetting form with job data:', job);
      reset({
        description: job.description || '',
        location_id: job.location_id || 0,
        setup: secondsToMinutes(job.setup || 0),
        service: secondsToMinutes(job.service || 300),
        delivery: job.delivery || [],
        pickup: job.pickup || [],
        skills: job.skills || [],
        priority: job.priority || 3,
        time_windows: job.time_windows || [],
      });
    } else {
      // Reset to empty form for create mode
      console.log('🔄 [JobForm] Resetting form for create mode');
      reset({
        description: initialData?.description || '',
        location_id: initialData?.location_id || 0,
        setup: secondsToMinutes(initialData?.setup || 0),
        service: secondsToMinutes(initialData?.service || 300),
        delivery: initialData?.delivery || [],
        pickup: initialData?.pickup || [],
        skills: initialData?.skills || [],
        priority: initialData?.priority || 3,
        time_windows: initialData?.time_windows || [],
      });
    }
  }, [job, reset, initialData]);

  // Auto-populate coordinates when location is selected
  useEffect(() => {
    if (selectedLocation) {
      console.log(`Job will be created at location: ${selectedLocation.name} (${selectedLocation.location_lat}, ${selectedLocation.location_lon})`);
    }
  }, [selectedLocation]);

  const onSubmitHandler: SubmitHandler<JobFormData> = async (data: JobFormData) => {
    try {
      // If external onSubmit is provided, use it (for testing)
      if (onSubmit) {
        onSubmit(data);
        reset();
        onClose();
        return;
      }

      if (isEditMode) {
        // Update existing job
        if (!selectedLocation) {
          throw new Error('Location selection is required');
        }

        const jobUpdateData: JobUpdate = {
          description: data.description,
          location_id: data.location_id,
          setup: minutesToSeconds(data.setup),
          service: minutesToSeconds(data.service),
          delivery: data.delivery,
          pickup: data.pickup,
          skills: data.skills,
          priority: data.priority,
          time_windows: data.time_windows,
        };
        
        console.log('🔄 Updating job with data:', jobUpdateData);
        
        await updateJob.mutateAsync({
          id: job.id,
          projectId: projectId,
          updates: jobUpdateData
        });
        
        console.log('✅ Job updated successfully');
        reset();
        onClose();
        onSuccess?.();
      } else {
        // Internal submission logic for production use
        if (!selectedLocation) {
          throw new Error('Location selection is required');
        }

        const jobData: JobCreate = {
          description: data.description, // Ensure description is required
          project_id: projectId,
          scenario_id: typeof currentScenario?.id === 'number' ? currentScenario.id : (() => {
            console.error('❌ [JobForm] Invalid scenario context:', currentScenario);
            throw new Error('Invalid scenario context: scenario ID is not a number');
          })(),
          dataset_id: typeof currentDataset?.id === 'number' ? currentDataset.id : (() => {
            console.error('❌ [JobForm] Invalid dataset context:', currentDataset);
            throw new Error('Invalid dataset context: dataset ID is not a number');
          })(),
          location_id: data.location_id,
          // Coordinates are derived from selected location - no manual entry
          location_lon: selectedLocation.location_lon,
          location_lat: selectedLocation.location_lat,
          setup: minutesToSeconds(data.setup),
          service: minutesToSeconds(data.service),
          delivery: data.delivery,
          pickup: data.pickup,
          skills: data.skills,
          priority: data.priority,
          time_windows: data.time_windows,
        };
        
        await createJob.mutateAsync(jobData);
        reset();
        onClose();
        onSuccess?.();
      }
    } catch (error) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} job:`, error);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  // Sample skills data for jobs - in a real app, this would come from an API
  const availableSkills: Skill[] = [
    { id: 1, name: 'Heavy Lifting', description: 'Requires heavy lifting capability', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
    { id: 2, name: 'Refrigerated Transport', description: 'Temperature-controlled items', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
    { id: 3, name: 'Hazmat Certified', description: 'Hazardous materials handling required', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
    { id: 4, name: 'Fragile Items', description: 'Special care for delicate items', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
    { id: 5, name: 'Express Delivery', description: 'Time-critical delivery', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
    { id: 6, name: 'Medical Supplies', description: 'Medical or pharmaceutical items', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
    { id: 7, name: 'Installation Required', description: 'On-site installation service', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
  ];

  // Priority descriptions for better UX
  const priorityDescriptions = {
    1: 'Lowest - Can be delayed if needed',
    2: 'Low - Standard scheduling',
    3: 'Normal - Default priority',
    4: 'High - Expedited handling',
    5: 'Critical - Must be completed urgently'
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {isEditMode ? `Edit Job: ${job?.description}` : 'Add New Job'}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmitHandler)}>
        <DialogContent sx={{ maxHeight: '80vh', overflowY: 'auto' }}>
          {!hasValidContext && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="600" gutterBottom>
                Missing Context: Cannot create job
              </Typography>
              <Typography variant="caption" display="block">
                Please select a scenario and dataset first:
                {!currentScenario && " • No scenario selected"}
                {!currentDataset && " • No dataset selected"}
              </Typography>
              <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                Use the breadcrumbs above to navigate to Project → Scenario → Dataset first.
              </Typography>
            </Alert>
          )}
          
          {(createJob.error || updateJob.error) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {isEditMode 
                ? (updateJob.error instanceof Error ? updateJob.error.message : 'Failed to update job')
                : (createJob.error instanceof Error ? createJob.error.message : 'Failed to create job')
              }
            </Alert>
          )}
          
          {/* Basic Information Section */}
          <FormSection title="Basic Information" subtitle="Job identification and location" required>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 8 }}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Job Description"
                      fullWidth
                      error={!!errors.description}
                      helperText={errors.description?.message}
                      placeholder="e.g., Delivery to Customer A, Service Call #123"
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                  name="priority"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.priority}>
                      <InputLabel>Priority *</InputLabel>
                      <Select {...field} label="Priority *">
                        {Object.entries(priorityDescriptions).map(([value, description]) => (
                          <MenuItem key={value} value={parseInt(value)}>
                            <Box>
                              <Typography variant="body1">{value} - {description.split(' - ')[0]}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {description.split(' - ')[1]}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.priority && (
                        <FormHelperText>{errors.priority.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>
          </FormSection>

          {/* Location Configuration Section */}
          <FormSection title="Location Assignment" subtitle="Job location from master locations" required>
            <Controller
              name="location_id"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.location_id}>
                  <InputLabel>Location *</InputLabel>
                  <Select
                    {...field}
                    label="Location *"
                    disabled={locationsLoading}
                  >
                    <MenuItem value={0} disabled>
                      <Typography color="text.secondary">
                        {locationsLoading ? 'Loading locations...' : 'Select a location'}
                      </Typography>
                    </MenuItem>
                    {safeLocations.map((location) => (
                      <MenuItem key={location.id} value={location.id}>
                        <Box>
                          <Typography variant="body1">{location.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {location.address || `${location.location_lat.toFixed(4)}, ${location.location_lon.toFixed(4)}`}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.location_id && (
                    <FormHelperText>{errors.location_id.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />

            {/* Selected Location Display */}
            {selectedLocation && (
              <Paper sx={{ p: 2, mt: 2, bgcolor: 'info.light', border: '1px solid', borderColor: 'info.main' }}>
                <Typography variant="subtitle2" color="info.dark">
                  Selected Location Details:
                </Typography>
                <Typography variant="body2">
                  <strong>Name:</strong> {selectedLocation.name}
                </Typography>
                <Typography variant="body2">
                  <strong>Coordinates:</strong> {selectedLocation.location_lat.toFixed(6)}, {selectedLocation.location_lon.toFixed(6)}
                </Typography>
                {selectedLocation.address && (
                  <Typography variant="body2">
                    <strong>Address:</strong> {selectedLocation.address}
                  </Typography>
                )}
              </Paper>
            )}

            {/* Location Master Integration Alert */}
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Location Master Integration:</strong> This job will be created at the selected location. 
                Coordinates are automatically populated from the location master to maintain data integrity.
              </Typography>
            </Alert>
          </FormSection>

          {/* Service Time Configuration Section */}
          <FormSection title="Service Configuration" subtitle="Setup and service time requirements">
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="setup"
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <TextField
                      {...field}
                      value={value}
                      onChange={(e) => onChange(parseInt(e.target.value) || 0)}
                      label={getTimeFieldLabel('setup')}
                      type="number"
                      fullWidth
                      error={!!errors.setup}
                      helperText={errors.setup?.message || getTimeFieldHelperText('setup')}
                      inputProps={{ min: 0, step: 1 }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="service"
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <TextField
                      {...field}
                      value={value}
                      onChange={(e) => onChange(parseInt(e.target.value) || 0)}
                      label={getTimeFieldLabel('service')}
                      type="number"
                      fullWidth
                      error={!!errors.service}
                      helperText={errors.service?.message || getTimeFieldHelperText('service')}
                      inputProps={{ min: 0, step: 1 }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </FormSection>

          {/* Delivery & Pickup Configuration Section */}
          <FormSection title="Cargo Configuration" subtitle="Multi-dimensional delivery and pickup amounts" defaultExpanded={false}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="delivery"
                  control={control}
                  render={({ field }) => (
                    <CapacityEditor
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.delivery?.message}
                      label="Delivery Amounts"
                      helperText="Items to be delivered (weight, volume, count, etc.)"
                      minDimensions={0}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="pickup"
                  control={control}
                  render={({ field }) => (
                    <CapacityEditor
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.pickup?.message}
                      label="Pickup Amounts"
                      helperText="Items to be picked up (weight, volume, count, etc.)"
                      minDimensions={0}
                    />
                  )}
                />
              </Grid>
            </Grid>
            
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Multi-dimensional Cargo:</strong> Define delivery and pickup amounts across multiple dimensions 
                (e.g., weight in kg, volume in m³, item count). Leave empty if no cargo handling is required.
              </Typography>
            </Alert>
          </FormSection>

          {/* Time Windows Section */}
          <FormSection title="Time Constraints" subtitle="Define when this job can be performed" defaultExpanded={false}>
            <Controller
              name="time_windows"
              control={control}
              render={({ field }) => (
                <TimeWindowsEditor
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.time_windows?.message}
                  label="Available Time Windows"
                  helperText="Add time periods when this job can be performed. Leave empty for no time restrictions."
                />
              )}
            />
          </FormSection>

          {/* Skills Requirements Section */}
          <FormSection title="Skill Requirements" subtitle="Required capabilities for this job" defaultExpanded={false}>
            <Controller
              name="skills"
              control={control}
              render={({ field }) => (
                <SkillsSelector
                  selectedSkills={field.value || []}
                  onSkillsChange={field.onChange}
                  skills={availableSkills}
                  error={!!errors.skills}
                  label="Required Skills"
                  helperText="Select skills that vehicles must have to perform this job"
                />
              )}
            />
          </FormSection>

          {/* Warning for missing locations */}
          {!safeLocations.length && !locationsLoading && (
            <Alert severity="warning">
              <Typography variant="body2">
                <strong>No locations available.</strong> Please create locations first before adding jobs.
                Jobs must be associated with existing locations to maintain the location master integrity.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained"
            disabled={!hasValidContext || isSubmitting || !safeLocations.length || selectedLocationId === 0 || updateJob.isPending}
            sx={{ minWidth: 120 }}
          >
            {(isSubmitting || updateJob.isPending) ? <CircularProgress size={20} /> : (isEditMode ? 'Update Job' : 'Add Job')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};